import React from 'react'
import type { Invite } from '@matech/thebigpos-sdk'
import { useIntl } from 'react-intl'
import { Send as SendIcon } from '@mui/icons-material'
import { useQuery } from '@tanstack/react-query'
import DataTable from '../DataTable'
import { getTheme } from '../../config'
import { formatDate } from '../../services/helper'
import ButtonGroup from '../Button/ButtonGroup'
import { useLoanContext } from '../../contexts/LoanContext'
import useInviteUser from '../../hooks/useInviteUser'
import { TheBigPOSApi } from '../../lib/TheBigPOSClient'
import queryKeys from '../../services/queryKeys'
import { applyMask } from '../../services/utils'
import { addSpacesToPascalCase } from '../../services/helpers'
import { Button } from '../Button'
import useUser from '../../hooks/useUser'

const theme = getTheme()

export type LoanInvitesTableProps = {
	title?: string
}

const DEFAULT_SORT_COLUMN = 'createdAt'

const LoanInvitesTable = ({ title }: LoanInvitesTableProps) => {
	const { dataTable: breakpoint } = theme.breakpoints

	const { formatMessage } = useIntl()
	const { loanId } = useLoanContext()
	const { resendInvite, resendInviteMutation } = useInviteUser()
	const { isBorrower } = useUser()

	const {
		data: loanInvites,
		isLoading,
		isRefetching,
		refetch,
	} = useQuery({
		queryKey: [queryKeys.loanInvites, loanId],
		queryFn: () => TheBigPOSApi.getLoanInvites(loanId),
		enabled: !!loanId,
	})

	const invites = loanInvites?.data

	const columns = [
		{
			name: formatMessage({ id: 'global.name' }),
			selector: (row: Invite) => `${row.firstName} ${row.lastName}`,
			cell: (row: Invite) => `${row.firstName} ${row.lastName}`,
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.email' }),
			selector: (row: Invite) => row.emailAddress,
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.phone_number' }),
			cell: (row: Invite) => applyMask(row.phoneNumber, 'phone'),
		},
		{
			name: formatMessage({ id: 'global.role' }),
			selector: (row: Invite) =>
				row.loanRole
					? addSpacesToPascalCase(row.loanRole)
					: formatMessage({ id: 'loans.not_assigned' }),
			sortable: true,
		},
		{
			name: formatMessage({ id: 'global.created' }),
			selector: (row: Invite) => row.createdAt,
			cell: (row: Invite) => formatDate(row.createdAt, false),
			sortable: true,
			sortField: 'createdAt',
			hide: breakpoint,
		},
		{
			name: '',
			omit: isBorrower,
			cell: (row: Invite) => {
				const handleInviteClick = () => {
					resendInvite(row)
				}
				return (
					<ButtonGroup
						overflowLimit={1}
						items={[
							<Button
								key="LoanContactsTable__invite"
								onClick={handleInviteClick}
								startIcon={<SendIcon />}
								text="Send Invite"
								variant="text"
								className="dark:!text-white"
							/>,
						]}
					/>
				)
			},
		},
	]

	return (
		<div>
			<DataTable
				title={title}
				onRefreshClick={refetch}
				data={invites || []}
				columns={columns}
				defaultSortAsc={false}
				defaultSortFieldId={DEFAULT_SORT_COLUMN}
				progressPending={isLoading && !isRefetching}
				refreshing={resendInviteMutation.isPending}
				keyField="id"
			/>
		</div>
	)
}

export default LoanInvitesTable
